import { useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Check,
  X,
  Target,
  MessageSquare,
  Calendar,
  TrendingUp,
  Zap,
  ShieldCheck,
} from "lucide-react";
import FundProjectCTA from "@/components/site/FundProjectCTA";

const STOP_WORDS = new Set([
  "the","a","an","and","or","but","if","then","of","to","in","on","for","with",
  "at","by","from","up","about","into","over","after","is","are","was","were",
  "be","been","being","this","that","these","those","it","its","as","not",
  "we","you","your","our","us","i","me","my","he","she","they","them","their",
  "will","can","just","get","have","has","had","do","does","did","so","now",
  "very","more","most","some","any","all","one","only","also","than","when",
  "how","what","why","who","which","where","who","dan","yang","untuk","atau",
  "kita","kami","kamu","dari","dengan","ini","itu","tidak","pada","juga",
]);

const tokens = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((w) => w.length > 3 && !STOP_WORDS.has(w));

const scoreColor = (score) => {
  if (score >= 8) return "text-green-500 dark:text-green-400";
  if (score >= 6) return "text-yellow-600 dark:text-yellow-500";
  if (score >= 4) return "text-orange-500 dark:text-orange-400";
  return "text-red-500 dark:text-red-400";
};

const scoreLabel = (score) => {
  if (score >= 9) return "Winner";
  if (score >= 7) return "Strong";
  if (score >= 5) return "Average";
  if (score >= 3) return "Weak";
  return "Broken";
};

function computeBasicAnalysis(ad) {
  const title = (ad?.title || "").trim();
  const body = (ad?.body || "").trim();
  const now = Date.now() / 1000;
  const startTs = ad?.start_date_ts || null;
  const ageDays = startTs ? Math.max(0, Math.round((now - startTs) / 86400)) : null;

  const bodyLen = body.length;
  const titleLen = title.length;

  const strengths = [];
  const weaknesses = [];
  let score = 5;

  // Text length signal
  if (bodyLen >= 80 && bodyLen <= 300) {
    score += 2;
    strengths.push(`Ad copy length (${bodyLen} chars) sits in the proven Meta sweet spot of 80–300 characters.`);
  } else if (bodyLen > 0 && bodyLen < 40) {
    score -= 1;
    weaknesses.push(`Ad copy is very short (${bodyLen} chars) — Meta users typically need more context to convert.`);
  } else if (bodyLen > 500) {
    score -= 1;
    weaknesses.push(`Ad copy is long (${bodyLen} chars). Consider trimming — attention drops sharply after 300 chars in-feed.`);
  } else if (bodyLen === 0) {
    weaknesses.push("No body copy detected — you're relying entirely on the creative to sell.");
  }

  // Title / body keyword overlap
  const tTokens = new Set(tokens(title));
  const bTokens = tokens(body);
  const overlap = [...new Set(bTokens.filter((w) => tTokens.has(w)))];
  if (title && body) {
    strengths.push("Ad has both a headline and body — reinforced message architecture.");
  } else if (!title && body) {
    weaknesses.push("Missing a headline — you're leaving conversion signal on the table.");
  }
  if (overlap.length >= 2) {
    score += 2;
    strengths.push(
      `Strong headline↔body alignment: shared keywords include "${overlap.slice(0, 3).join('", "')}". Consistency drives recall.`
    );
  } else if (title && body && overlap.length === 0) {
    score -= 1;
    weaknesses.push("Headline and body share no keywords — messaging feels disjointed.");
  }

  // Ad age signal (longer = winner)
  let ageBadge = null;
  if (ageDays !== null) {
    if (ageDays > 30) {
      score += 3;
      ageBadge = { label: "PROVEN WINNER", tone: "green" };
      strengths.push(
        `Ad has been running ${ageDays} days — advertisers only keep ads live this long when they convert.`
      );
    } else if (ageDays > 14) {
      score += 1;
      ageBadge = { label: "SCALING", tone: "yellow" };
      strengths.push(`Live for ${ageDays} days — past the initial test window, showing promise.`);
    } else if (ageDays >= 7) {
      ageBadge = { label: "TESTING", tone: "orange" };
      weaknesses.push(`Only ${ageDays} days live — still inside typical test window.`);
    } else {
      ageBadge = { label: "FRESH", tone: "red" };
      weaknesses.push(`Very new (${ageDays} days) — no signal on performance yet.`);
    }
  }

  // Advertiser context
  if (ad?.page_name) {
    strengths.push(`Clear advertiser attribution (${ad.page_name}) — builds trust.`);
  }

  // Clamp
  score = Math.max(1, Math.min(10, score));

  const verdict = (() => {
    if (score >= 8) return "High-performing signal — worth swiping and modeling closely.";
    if (score >= 6) return "Solid ad. Copy the structure, punch up your own hook.";
    if (score >= 4) return "Middling — decent bones, but hook and CTA could be stronger.";
    return "Weak signals across copy, structure, and longevity.";
  })();

  return { score, verdict, strengths, weaknesses, ageDays, bodyLen, titleLen, overlap, ageBadge };
}

export default function AdAnalysis({ ad }) {
  const [analysis, setAnalysis] = useState(null);
  const [computing, setComputing] = useState(false);

  const canAnalyze = Boolean((ad?.body || ad?.title || "").trim());

  const handleAnalyze = () => {
    if (!canAnalyze) {
      toast.error("This ad has no text to analyze");
      return;
    }
    setComputing(true);
    setTimeout(() => {
      setAnalysis(computeBasicAnalysis(ad));
      setComputing(false);
    }, 350);
  };

  if (!ad) return null;

  const ageDate =
    ad.start_date_ts ? new Date(ad.start_date_ts * 1000).toLocaleDateString() : null;

  return (
    <div className="mt-10 border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
      <div className="p-6 md:p-8 border-b border-zinc-200 dark:border-zinc-800 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-yellow-500 mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            // Instant ad teardown
          </p>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-zinc-900 dark:text-white tracking-tight">
            Is this ad any good?
          </h3>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 max-w-md">
            Heuristic scoring on copy length, headline↔body alignment, and ad
            age. Runs in your browser — no AI, no credits, no wait.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 border border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest">
            <ShieldCheck className="w-3 h-3" />
            100% Free · No AI credits used
          </div>
        </div>
        <button
          data-testid="analyze-ad-btn"
          onClick={handleAnalyze}
          disabled={computing || !canAnalyze}
          className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold h-12 px-6 transition-all hover:-translate-y-0.5"
        >
          <Zap className="w-4 h-4" strokeWidth={2.5} />
          {computing ? "Scoring…" : analysis ? "Re-score" : "Score this ad"}
        </button>
      </div>

      {!analysis && !computing && (
        <div className="p-10 text-center text-zinc-500 flex flex-col items-center gap-3">
          <TrendingUp className="w-8 h-8 text-zinc-300 dark:text-zinc-700" />
          <p className="text-sm max-w-md">
            Click <span className="text-yellow-600 dark:text-yellow-500 font-semibold">Score this ad</span>{" "}
            to run the instant heuristic teardown.
          </p>
        </div>
      )}

      {computing && (
        <div data-testid="analysis-loading" className="p-10 grid place-items-center text-zinc-500">
          <span className="font-mono text-xs uppercase tracking-widest animate-pulse">
            Computing signals…
          </span>
        </div>
      )}

      {analysis && !computing && (
        <div data-testid="analysis-result" className="p-6 md:p-8">
          {/* Score + verdict */}
          <div className="grid grid-cols-12 gap-6 items-center border-b border-zinc-200 dark:border-zinc-800 pb-8">
            <div className="col-span-12 md:col-span-4 border border-zinc-200 dark:border-zinc-800 p-6 text-center">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                Ad score
              </p>
              <div className="mt-2 flex items-baseline justify-center gap-1">
                <span
                  data-testid="analysis-score"
                  className={`font-display font-extrabold text-7xl md:text-8xl tracking-tight ${scoreColor(analysis.score)}`}
                >
                  {analysis.score}
                </span>
                <span className="font-display font-bold text-2xl text-zinc-400 dark:text-zinc-600">
                  /10
                </span>
              </div>
              <div className="mt-2 inline-block bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 px-3 py-1">
                <span className={`text-xs font-mono uppercase tracking-widest ${scoreColor(analysis.score)}`}>
                  {scoreLabel(analysis.score)}
                </span>
              </div>
            </div>
            <div className="col-span-12 md:col-span-8">
              <p className="text-xs font-mono uppercase tracking-widest text-yellow-600 dark:text-yellow-500 mb-2">
                Verdict
              </p>
              <p
                data-testid="analysis-verdict"
                className="font-display font-bold text-xl md:text-3xl text-zinc-900 dark:text-white leading-tight tracking-tight"
              >
                &ldquo;{analysis.verdict}&rdquo;
              </p>
            </div>
          </div>

          {/* Signal cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-zinc-200 dark:border-zinc-800">
            <SignalCard
              icon={MessageSquare}
              label="Copy length"
              value={`${analysis.bodyLen} chars`}
              hint={
                analysis.bodyLen >= 80 && analysis.bodyLen <= 300
                  ? "Sweet spot"
                  : analysis.bodyLen < 40
                    ? "Too short"
                    : analysis.bodyLen > 500
                      ? "Too long"
                      : "OK"
              }
              testId="signal-length"
              className="md:border-r border-zinc-200 dark:border-zinc-800"
            />
            <SignalCard
              icon={Target}
              label="Title ↔ body overlap"
              value={`${analysis.overlap.length} shared keyword${analysis.overlap.length === 1 ? "" : "s"}`}
              hint={analysis.overlap.length >= 2 ? "Aligned" : "Weak alignment"}
              testId="signal-overlap"
              className="md:border-r border-t md:border-t-0 border-zinc-200 dark:border-zinc-800"
            />
            <SignalCard
              icon={Calendar}
              label="Ad age"
              value={
                analysis.ageDays !== null
                  ? `${analysis.ageDays} day${analysis.ageDays === 1 ? "" : "s"}`
                  : "Unknown"
              }
              hint={ageDate ? `Since ${ageDate}` : "No start date"}
              badge={analysis.ageBadge}
              testId="signal-age"
              className="border-t md:border-t-0 border-zinc-200 dark:border-zinc-800"
            />
          </div>

          {/* Strengths / weaknesses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="p-6 md:p-8 md:border-r border-zinc-200 dark:border-zinc-800">
              <p className="text-xs font-mono uppercase tracking-widest text-green-600 dark:text-green-400 mb-4 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Why it works
              </p>
              {analysis.strengths.length ? (
                <ul className="space-y-3">
                  {analysis.strengths.map((s, i) => (
                    <li
                      key={s}
                      data-testid={`analysis-strength-${i}`}
                      className="flex items-start gap-3 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed"
                    >
                      <span className="text-green-600 dark:text-green-400 mt-0.5">+</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-zinc-500">No strong positive signals detected.</p>
              )}
            </div>
            <div className="p-6 md:p-8 border-t md:border-t-0 border-zinc-200 dark:border-zinc-800">
              <p className="text-xs font-mono uppercase tracking-widest text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
                <X className="w-4 h-4" />
                Watch-outs
              </p>
              {analysis.weaknesses.length ? (
                <ul className="space-y-3">
                  {analysis.weaknesses.map((s, i) => (
                    <li
                      key={s}
                      data-testid={`analysis-weakness-${i}`}
                      className="flex items-start gap-3 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed"
                    >
                      <span className="text-red-600 dark:text-red-400 mt-0.5">−</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-zinc-500">No obvious weaknesses at a glance.</p>
              )}
            </div>
          </div>

          {/* Fund the project CTA — after the analysis result */}
          <FundProjectCTA />
        </div>
      )}
    </div>
  );
}

function SignalCard({ icon: Icon, label, value, hint, badge, testId, className }) {
  const toneMap = {
    green: "bg-green-500/10 text-green-600 dark:text-green-400",
    yellow: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-500",
    orange: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    red: "bg-red-500/10 text-red-600 dark:text-red-400",
  };
  return (
    <div
      data-testid={testId}
      className={`p-6 md:p-8 ${className || ""}`}
    >
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </p>
      <div className="font-display font-bold text-2xl md:text-3xl text-zinc-900 dark:text-white tracking-tight">
        {value}
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
          {hint}
        </span>
        {badge && (
          <span
            className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 ${toneMap[badge.tone] || toneMap.yellow}`}
          >
            {badge.label}
          </span>
        )}
      </div>
    </div>
  );
}
